import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { AppLayout, Need } from './components/layout';
import { ConfirmProvider, Spinner, ToastProvider } from './components/ui';
import { ApiError } from './lib/api';
import { AuthProvider, useAuth } from './lib/auth';
import type { HostKind } from './lib/tenant-host';
import { PortalEntrar, PortalHome, PortalLayout, PortalPrices, PortalRenewals, PortalSupport } from './pages/portal';
import { PartnerLinkPage, PartnerLoginPage, PartnerPanelPage, PortalPartners, PortalPartnerTerms } from './pages/partners';
import { PortalHelp, PortalHelpArticle } from './pages/portal-help';
import { PortalPrivacy, PortalTerms } from './pages/legal';
import { AccountsPage } from './pages/accounts';
import { AuditPage } from './pages/audit';
import { BillingPage } from './pages/billing';
import { PlatformCheckoutsPage, PlatformGatewaysPage, PlatformPromoCodesPage } from './pages/platform-billing';
import { PlatformAffiliatePage, PlatformAffiliatesPage } from './pages/platform-affiliates';
import { ConfirmEmailPage, ForgotPasswordPage, LoginPage, ResetPasswordPage, SignupPage } from './pages/auth';
import { CustomerPage, CustomersPage } from './pages/customers';
import { DashboardPage } from './pages/dashboard';
import { DocumentEditPage } from './pages/document-edit';
import { DocumentViewPage } from './pages/document-view';
import { DocumentsPage } from './pages/documents';
import { ExportsPage } from './pages/exports';
import { ProductPage, ProductsPage } from './pages/products';
import { PayablesPage, PurchaseEditPage, PurchasesPage, PurchaseViewPage, SupplierPaymentsPage } from './pages/purchases';
import { ReportsPage } from './pages/reports';
import { RecurringPage } from './pages/automation';
import { SeriesPage, SettingsPage } from './pages/settings';
import { CategoriesPage, InventoriesPage, InventoryPage, StockLevelsPage, StockMovementsPage, WarehousesPage } from './pages/stock';
import { CashPage } from './pages/cash';
import { SupplierPage, SuppliersPage } from './pages/suppliers';
import {
  PlatformAnnouncementsPage, PlatformAuditPage, PlatformOnly, PlatformOverviewPage, PlatformPaymentsPage, PlatformPlansPage, PlatformSystemPage,
  PlatformTenantPage, PlatformTenantsPage, PlatformUsersPage,
} from './pages/platform';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: false },
  },
});

// Site público no domínio principal makila.sistemamz.com: apresentação, preços, ajuda, suporte, entrada.
const portalRouter = createBrowserRouter([
  {
    element: <PortalLayout />,
    children: [
      { index: true, element: <PortalHome /> },
      { path: 'precos', element: <PortalPrices /> },
      { path: 'renovacoes', element: <PortalRenewals /> },
      { path: 'ajuda', element: <PortalHelp /> },
      { path: 'ajuda/:slug', element: <PortalHelpArticle /> },
      { path: 'suporte', element: <PortalSupport /> },
      { path: 'entrar', element: <PortalEntrar /> },
      { path: 'termos', element: <PortalTerms /> },
      { path: 'privacidade', element: <PortalPrivacy /> },
      { path: 'parceiros', element: <PortalPartners /> },
      { path: 'parceiros/termos', element: <PortalPartnerTerms /> },
      { path: 'parceiro/entrar', element: <PartnerLoginPage /> },
      { path: 'parceiro', element: <PartnerPanelPage /> },
      { path: 'p/:code', element: <PartnerLinkPage /> },
    ],
  },
  { path: '/registar', element: <SignupPage /> },
  { path: '/recuperar-password', element: <ForgotPasswordPage /> },
  { path: '/redefinir-password', element: <ResetPasswordPage /> },
  { path: '/confirmar-email', element: <ConfirmEmailPage /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);

// Aplicação: subdomínios de empresa e consola de administração (admin.).
const appRouter = createBrowserRouter([
  { path: '/entrar', element: <LoginPage /> },
  { path: '/registar', element: <SignupPage /> },
  { path: '/recuperar-password', element: <ForgotPasswordPage /> },
  { path: '/redefinir-password', element: <ResetPasswordPage /> },
  { path: '/confirmar-email', element: <ConfirmEmailPage /> },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <DashboardPage /> },
      // Vendas
      { path: 'vendas', element: <Need perm="documents:view"><DocumentsPage /></Need> },
      { path: 'vendas/avencas', element: <Need perm="documents:view"><RecurringPage /></Need> },
      { path: 'documentos', element: <Navigate to="/vendas" replace /> },
      { path: 'documentos/novo', element: <Need perm="documents:create"><DocumentEditPage /></Need> },
      { path: 'documentos/:id', element: <Need perm="documents:view"><DocumentViewPage /></Need> },
      { path: 'documentos/:id/editar', element: <Need perm="documents:edit"><DocumentEditPage /></Need> },
      // Clientes
      { path: 'clientes', element: <Need perm="customers:view"><CustomersPage /></Need> },
      { path: 'clientes/:id', element: <Need perm="customers:view"><CustomerPage /></Need> },
      { path: 'contas-correntes', element: <Need perm="accounts:view"><AccountsPage /></Need> },
      { path: 'caixa', element: <Need perm="cash:view"><CashPage /></Need> },
      // Compras
      { path: 'compras', element: <Need perm="purchases:view"><PurchasesPage /></Need> },
      { path: 'compras/nova', element: <Need perm="purchases:create"><PurchaseEditPage /></Need> },
      { path: 'compras/pagamentos', element: <Need perm="purchases:view"><SupplierPaymentsPage /></Need> },
      { path: 'compras/contas-a-pagar', element: <Need perm="purchases:view"><PayablesPage /></Need> },
      { path: 'compras/:id', element: <Need perm="purchases:view"><PurchaseViewPage /></Need> },
      { path: 'compras/:id/editar', element: <Need perm="purchases:edit"><PurchaseEditPage /></Need> },
      { path: 'fornecedores', element: <Need perm="purchases:view"><SuppliersPage /></Need> },
      { path: 'fornecedores/:id', element: <Need perm="purchases:view"><SupplierPage /></Need> },
      // Stock
      { path: 'artigos', element: <Need perm="products:view"><ProductsPage /></Need> },
      { path: 'artigos/:id', element: <Need perm="products:view"><ProductPage /></Need> },
      { path: 'stock', element: <Need perm="stock:view"><StockLevelsPage /></Need> },
      { path: 'stock/movimentos', element: <Need perm="stock:view"><StockMovementsPage /></Need> },
      { path: 'stock/inventarios', element: <Need perm="stock:view"><InventoriesPage /></Need> },
      { path: 'stock/inventarios/:id', element: <Need perm="stock:view"><InventoryPage /></Need> },
      { path: 'stock/armazens', element: <Need perm="stock:view"><WarehousesPage /></Need> },
      { path: 'stock/categorias', element: <Need perm="products:view"><CategoriesPage /></Need> },
      // Relatórios, fiscal, definições
      { path: 'relatorios', element: <Need perm="reports:view"><ReportsPage /></Need> },
      { path: 'comunicacao-at', element: <Need perm="exports:view"><ExportsPage /></Need> },
      { path: 'fiscal/series', element: <Need perm="settings:view"><SeriesPage /></Need> },
      { path: 'auditoria', element: <Need perm="audit:view"><AuditPage /></Need> },
      { path: 'definicoes', element: <SettingsPage /> },
      { path: 'subscricao', element: <Need perm="billing:view"><BillingPage /></Need> },
      // Consola da plataforma (administração do Makila)
      { path: 'plataforma', element: <PlatformOnly><PlatformOverviewPage /></PlatformOnly> },
      { path: 'plataforma/empresas', element: <PlatformOnly><PlatformTenantsPage /></PlatformOnly> },
      { path: 'plataforma/empresas/:id', element: <PlatformOnly><PlatformTenantPage /></PlatformOnly> },
      { path: 'plataforma/pagamentos', element: <PlatformOnly><PlatformPaymentsPage /></PlatformOnly> },
      { path: 'plataforma/cobrancas', element: <PlatformOnly><PlatformCheckoutsPage /></PlatformOnly> },
      { path: 'plataforma/gateways', element: <PlatformOnly><PlatformGatewaysPage /></PlatformOnly> },
      { path: 'plataforma/codigos-promocionais', element: <PlatformOnly><PlatformPromoCodesPage /></PlatformOnly> },
      { path: 'plataforma/parceiros', element: <PlatformOnly><PlatformAffiliatesPage /></PlatformOnly> },
      { path: 'plataforma/parceiros/:id', element: <PlatformOnly><PlatformAffiliatePage /></PlatformOnly> },
      { path: 'plataforma/planos', element: <PlatformOnly><PlatformPlansPage /></PlatformOnly> },
      { path: 'plataforma/utilizadores', element: <PlatformOnly><PlatformUsersPage /></PlatformOnly> },
      { path: 'plataforma/avisos', element: <PlatformOnly><PlatformAnnouncementsPage /></PlatformOnly> },
      { path: 'plataforma/sistema', element: <PlatformOnly><PlatformSystemPage /></PlatformOnly> },
      { path: 'plataforma/auditoria', element: <PlatformOnly><PlatformAuditPage /></PlatformOnly> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

const ROUTERS: Record<HostKind, ReturnType<typeof createBrowserRouter>> = {
  portal: portalRouter,
  admin: appRouter,
  company: appRouter,
};

/** Escolhe o router pelo tipo de host, depois de a sessão/host resolverem. */
function Boot() {
  const { hostKind, loading } = useAuth();
  const router = useMemo(() => ROUTERS[hostKind], [hostKind]);
  if (loading) return <div className="grid h-dvh place-items-center"><Spinner className="size-7" /></div>;
  return <RouterProvider router={router} />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
            <Boot />
          </AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
